<template>
  <b-modal ref="modal" size="xl" hide-footer no-close-on-backdrop title="Transfer All to Another Branch" @hidden="stopProgressPolling">
    <!-- Step 1: pick destination -->
    <div v-if="step === 'select'">
      <p class="text-muted">
        Every in-stock product here that matches a product's name at the destination branch will be
        transferred in full. This can only be done by an Admin and requires a confirmation code sent
        to your phone before anything moves.
      </p>
      <div class="form-group">
        <label>Destination Branch</label>
        <select class="form-control" v-model="destinationBranch">
          <option value="">Select destination branch</option>
          <option v-for="b in otherBranches" :key="branchId(b)" :value="branchId(b)">
            {{ branchName(b) }}
          </option>
        </select>
      </div>
      <div v-if="error" class="text-danger mb-2">{{ error }}</div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-light mr-2" @click="$refs.modal.hide()">Cancel</button>
        <button class="btn btn-primary" :disabled="!destinationBranch || loading" @click="loadPreview">
          <span v-if="loading"><i class="fa fa-spinner fa-spin mr-1"></i>Analyzing...</span>
          <span v-else>Next: Review</span>
        </button>
      </div>
    </div>

    <!-- Step 2: review matched items, edit quantities -->
    <div v-else-if="step === 'review'">
      <p>
        <strong>{{ matched.length }}</strong> item(s) matched by name and will be transferred in full
        by default — you can edit quantities below.
        <span v-if="unmatched.length">
          <strong>{{ unmatched.length }}</strong> item(s) have no match at the destination branch —
          you'll be able to resolve these right after the transfer.
        </span>
      </p>
      <div class="table-responsive" style="max-height:50vh;overflow-y:auto;">
        <table class="table table-sm">
          <thead>
            <tr><th>Product</th><th>Source Qty</th><th>Destination Qty</th><th>Transfer Qty</th></tr>
          </thead>
          <tbody>
            <tr v-for="m in matched" :key="m.source_product_id">
              <td>{{ m.name }}</td>
              <td>{{ m.source_quantity }}</td>
              <td>{{ m.destination_quantity }}</td>
              <td style="min-width:160px;">
                <input
                  type="number"
                  class="form-control"
                  :class="{ 'is-invalid': qtyError(m) }"
                  v-model.number="m.quantity"
                  min="0.01"
                  :max="m.source_quantity"
                  step="any"
                />
                <div v-if="qtyError(m)" class="invalid-feedback">{{ qtyError(m) }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="error" class="text-danger mb-2">{{ error }}</div>
      <div class="form-group">
        <label class="d-block">Send confirmation code via</label>
        <div class="btn-group btn-group-toggle">
          <label class="btn btn-outline-secondary" :class="{ active: otpChannel === 'sms' }">
            <input type="radio" value="sms" v-model="otpChannel" autocomplete="off" /> SMS
          </label>
          <label class="btn btn-outline-secondary" :class="{ active: otpChannel === 'email' }">
            <input type="radio" value="email" v-model="otpChannel" autocomplete="off" /> Email
          </label>
        </div>
      </div>
      <div class="d-flex justify-content-between align-items-center mt-2">
        <button class="btn btn-secondary" @click="step = 'select'">Back</button>
        <button class="btn btn-primary" :disabled="!matched.length || hasQtyErrors || loading" @click="requestOtp">
          <span v-if="loading"><i class="fa fa-spinner fa-spin mr-1"></i>Sending code...</span>
          <span v-else>Confirm &amp; Send Code</span>
        </button>
      </div>
    </div>

    <!-- Step 3: OTP confirmation -->
    <div v-else-if="step === 'otp'">
      <p>
        Enter the 6-digit confirmation code sent to
        <strong>{{ destinationMasked || (otpChannel === 'email' ? 'your email' : 'your phone') }}</strong>
        to transfer
        <strong>{{ matched.length }}</strong> item(s) to <strong>{{ destinationBranchName }}</strong>.
        This cannot be undone.
      </p>
      <div class="form-group" style="max-width:220px;">
        <label>Confirmation Code</label>
        <input
          type="text"
          inputmode="numeric"
          maxlength="6"
          class="form-control"
          v-model="otp"
          placeholder="123456"
        />
      </div>
      <div v-if="error" class="text-danger mb-2">{{ error }}</div>
      <div class="d-flex justify-content-between align-items-center">
        <button class="btn btn-link px-0" :disabled="loading" @click="requestOtp">Resend code</button>
        <div>
          <button class="btn btn-secondary mr-2" :disabled="loading" @click="step = 'review'">Back</button>
          <button class="btn btn-danger" :disabled="otp.length !== 6 || loading" @click="execute">
            <span v-if="loading"><i class="fa fa-spinner fa-spin mr-1"></i>Transferring...</span>
            <span v-else>Transfer All</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Step 4: processing (background jobs are moving stock) -->
    <div v-else-if="step === 'processing'">
      <div class="text-center py-4">
        <div class="spinner-border text-primary mb-3" role="status"></div>
        <p>
          Transferring <strong>{{ progress.completed }}</strong> of <strong>{{ progress.total }}</strong>
          item(s) to <strong>{{ destinationBranchName }}</strong>...
        </p>
        <div class="progress mb-3" style="height:10px;">
          <div
            class="progress-bar"
            role="progressbar"
            :style="{ width: progressPercent + '%' }"
            :aria-valuenow="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <p class="text-muted small mb-0">
          This runs in the background — it's safe to close this window, the transfer keeps going.
          Check back anytime from the Transfers page.
        </p>
      </div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-light" @click="$refs.modal.hide()">Close (keep running)</button>
      </div>
    </div>

    <!-- Step 5: report -->
    <div v-else-if="step === 'report'">
      <div class="alert alert-success">
        Transferred <strong>{{ report.transferredCount }}</strong> item(s) to
        <strong>{{ destinationBranchName }}</strong> (Transfer {{ report.transferCode }}).
      </div>

      <div v-if="droppedTaxesWarning.length" class="alert alert-warning">
        Some taxes couldn't be matched at the destination branch and were left off the newly created
        product(s), so double-check tax settings before selling them: {{ droppedTaxesWarning.join('; ') }}
      </div>

      <div v-if="pendingItems.length">
        <h6>Pending items — no match found at destination ({{ pendingCount }} left)</h6>
        <div class="table-responsive" style="max-height:40vh;overflow-y:auto;">
          <table class="table table-sm">
            <thead><tr><th>Product</th><th>Qty</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <tr v-for="p in pendingItems" :key="p.id">
                <td>{{ p.product_name }}</td>
                <td>{{ p.quantity }}</td>
                <td>
                  <span v-if="p.status === 'pending'" class="badge badge-warning">Pending</span>
                  <span v-else-if="p.status === 'created'" class="badge badge-success">Created &amp; Transferred</span>
                  <span v-else class="badge badge-secondary">Skipped</span>
                </td>
                <td v-if="p.status === 'pending'">
                  <button
                    class="btn btn-sm btn-outline-primary mr-1"
                    :disabled="resolvingId === p.id || bulkResolving"
                    @click="createAndTransfer(p)"
                  >Create &amp; Transfer</button>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="resolvingId === p.id || bulkResolving"
                    @click="skip(p)"
                  >Skip</button>
                </td>
                <td v-else></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="pendingCount > 0" class="d-flex justify-content-end mb-2">
          <button class="btn btn-sm btn-outline-secondary mr-2" :disabled="bulkResolving" @click="skipAll">
            Skip All Remaining
          </button>
          <button class="btn btn-sm btn-primary" :disabled="bulkResolving" @click="createAndTransferAll">
            <span v-if="bulkResolving"><i class="fa fa-spinner fa-spin mr-1"></i>Working...</span>
            <span v-else>Create &amp; Transfer All Pending</span>
          </button>
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <button class="btn btn-primary" @click="done">Done</button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TransferAllModal',

  data() {
    return {
      step: 'select',
      loading: false,
      bulkResolving: false,
      resolvingId: null,
      error: '',

      otherBranches: [],
      destinationBranch: '',

      matched: [],
      unmatched: [],
      otp: '',
      otpChannel: 'sms',
      destinationMasked: '',

      transferId: null,
      report: { transferredCount: 0, transferCode: '' },
      pendingItems: [],
      droppedTaxesWarning: [],

      progress: { total: 0, completed: 0, status: 'pending' },
      pollTimer: null,
    };
  },

  beforeDestroy() {
    this.stopProgressPolling();
  },

  computed: {
    destinationBranchName() {
      const b = this.otherBranches.find(b => this.branchId(b) === this.destinationBranch);
      return b ? this.branchName(b) : '';
    },
    hasQtyErrors() {
      return this.matched.some(m => this.qtyError(m));
    },
    pendingCount() {
      return this.pendingItems.filter(p => p.status === 'pending').length;
    },
    progressPercent() {
      if (!this.progress.total) return 0;
      return Math.min(100, Math.round((this.progress.completed / this.progress.total) * 100));
    },
  },

  methods: {
    branchId(b) {
      return b.company ? b.company.id : b.id;
    },
    branchName(b) {
      return b.company ? b.company.name : b.name;
    },
    qtyError(m) {
      const qty = Number(m.quantity);
      if (!Number.isFinite(qty) || qty <= 0) return 'Quantity must be greater than 0.';
      if (qty > m.source_quantity) return `Cannot exceed available stock (${m.source_quantity}).`;
      return null;
    },

    async open() {
      this.stopProgressPolling();
      this.step = 'select';
      this.loading = false;
      this.bulkResolving = false;
      this.resolvingId = null;
      this.error = '';
      this.destinationBranch = '';
      this.matched = [];
      this.unmatched = [];
      this.otp = '';
      this.otpChannel = 'sms';
      this.destinationMasked = '';
      this.transferId = null;
      this.report = { transferredCount: 0, transferCode: '' };
      this.pendingItems = [];
      this.droppedTaxesWarning = [];
      this.progress = { total: 0, completed: 0, status: 'pending' };

      await this.fetchOtherBranches();
      this.$nextTick(() => this.$refs.modal.show());
    },

    async fetchOtherBranches() {
      try {
        const res = await axios.get('/api/other-branches');
        this.otherBranches = res.data || [];
      } catch (e) {
        console.error('Failed to fetch branches', e);
        this.otherBranches = [];
      }
    },

    async loadPreview() {
      this.error = '';
      this.loading = true;
      try {
        const { data } = await axios.post('/api/transfer-stocks/bulk-all/preview', {
          destination_branch: this.destinationBranch,
        });
        this.matched = (data.matched || []).map(m => ({ ...m }));
        this.unmatched = data.unmatched || [];

        if (!this.matched.length && !this.unmatched.length) {
          this.error = 'No in-stock products found to transfer.';
          return;
        }

        this.step = 'review';
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not analyze branches.';
      } finally {
        this.loading = false;
      }
    },

    async requestOtp() {
      if (this.hasQtyErrors) return;
      this.error = '';
      this.loading = true;
      try {
        const { data } = await axios.post('/api/transfer-stocks/bulk-all/request-otp', {
          destination_branch: this.destinationBranch,
          channel: this.otpChannel,
        });
        this.otp = '';
        this.destinationMasked = data.destination_masked || '';
        this.step = 'otp';
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not send confirmation code.';
      } finally {
        this.loading = false;
      }
    },

    async execute() {
      this.error = '';
      this.loading = true;
      try {
        const payload = {
          destination_branch: this.destinationBranch,
          items: this.matched.map(m => ({ id: m.source_product_id, quantity: Number(m.quantity) })),
          otp: this.otp,
          channel: this.otpChannel,
        };
        const { data } = await axios.post('/api/transfer-stocks/bulk-all', payload);

        // Stock movement now happens in background jobs — the response here
        // just confirms the transfer was created and queued, not that items
        // moved yet. Pending (unmatched) items are known immediately since
        // matching happens before any stock moves.
        this.transferId = data.data.id;
        this.report.transferCode = data.data.transfer_code;
        this.pendingItems = data.data.pending_items || [];
        this.progress = { total: data.total_items || 0, completed: 0, status: data.data.status };

        this.step = 'processing';
        this.startProgressPolling();
      } catch (e) {
        this.error = e.response?.data?.message || 'Transfer failed.';
      } finally {
        this.loading = false;
      }
    },

    startProgressPolling() {
      this.stopProgressPolling();
      this.pollProgress();
      this.pollTimer = setInterval(this.pollProgress, 2500);
    },

    stopProgressPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async pollProgress() {
      if (!this.transferId) return;
      try {
        const { data } = await axios.get(`/api/transfer-stocks/${this.transferId}/progress`);
        this.progress = data;

        if (data.total > 0 && data.completed >= data.total) {
          this.stopProgressPolling();
          await this.finalizeReport();
        } else if (data.total === 0) {
          // No matched items were actually queued (shouldn't happen via the
          // normal review-step guard, but handle it rather than polling forever).
          this.stopProgressPolling();
          await this.finalizeReport();
        }
      } catch (e) {
        // Transient poll failure — try again on the next tick rather than
        // surfacing an error mid-run; the background jobs are unaffected.
      }
    },

    async finalizeReport() {
      try {
        const { data } = await axios.get(`/api/transfer-stocks/${this.transferId}`);
        this.report.transferredCount = (data.items || []).length;
      } catch (e) {
        this.report.transferredCount = this.progress.completed;
      } finally {
        this.step = 'report';
      }
    },

    async createAndTransfer(pending) {
      this.resolvingId = pending.id;
      try {
        const { data } = await axios.post(
          `/api/transfer-stocks/${this.transferId}/pending-items/${pending.id}/create-and-transfer`
        );
        pending.status = 'created';
        if (data.dropped_taxes && Object.keys(data.dropped_taxes).length) {
          this.mergeDroppedTaxes(data.dropped_taxes);
        }
      } catch (e) {
        this.$toasts?.push({ type: 'error', message: e.response?.data?.message || 'Could not resolve item.' });
      } finally {
        this.resolvingId = null;
      }
    },

    async skip(pending) {
      this.resolvingId = pending.id;
      try {
        await axios.post(`/api/transfer-stocks/${this.transferId}/pending-items/${pending.id}/skip`);
        pending.status = 'skipped';
      } catch (e) {
        this.$toasts?.push({ type: 'error', message: e.response?.data?.message || 'Could not skip item.' });
      } finally {
        this.resolvingId = null;
      }
    },

    async createAndTransferAll() {
      this.bulkResolving = true;
      try {
        const { data } = await axios.post(
          `/api/transfer-stocks/${this.transferId}/pending-items/create-and-transfer-bulk`
        );
        this.pendingItems.forEach(p => { if (p.status === 'pending') p.status = 'created'; });
        if (data.dropped_taxes && Object.keys(data.dropped_taxes).length) {
          this.mergeDroppedTaxes(data.dropped_taxes);
        }
      } catch (e) {
        this.$toasts?.push({ type: 'error', message: e.response?.data?.message || 'Could not resolve pending items.' });
      } finally {
        this.bulkResolving = false;
      }
    },

    async skipAll() {
      this.bulkResolving = true;
      try {
        await axios.post(`/api/transfer-stocks/${this.transferId}/pending-items/skip-bulk`);
        this.pendingItems.forEach(p => { if (p.status === 'pending') p.status = 'skipped'; });
      } catch (e) {
        this.$toasts?.push({ type: 'error', message: e.response?.data?.message || 'Could not skip remaining items.' });
      } finally {
        this.bulkResolving = false;
      }
    },

    mergeDroppedTaxes(droppedByProduct) {
      const lines = Object.entries(droppedByProduct).map(([product, taxes]) => `${product} (${taxes.join(', ')})`);
      this.droppedTaxesWarning = [...new Set([...this.droppedTaxesWarning, ...lines])];
    },

    done() {
      this.$refs.modal.hide();
      this.$emit('completed');
    },
  },
};
</script>
